import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { Loading } from '@liveauctioneers/hammer-ui-core/loading';
import { H2 } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { memo, ReactNode } from 'react';
import isEqual from 'react-fast-compare';

import { DiscoveryTags } from '@/components/DiscoveryTags/DiscoveryTags';
import { upcomingItemPageItemCard } from '@/components/GridItemCard';
import SaveSearchContainer from '@/components/SaveSearch/SaveSearchContainer';
import { CategoryLink } from '@/types/CategoryLink';
import { ItemFacets } from '@/types/ItemFacets';
import { Suggestions } from '@/types/SimilarItemInfo';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { getBroadTitleSearchParams, getSimilarItemsUrl } from '@/utils/urls';

import FluidItemGrid from '../GridItemCard/FluidItemGrid/FluidItemGrid';
import { SeeAllLink } from '../SeeAllLink/SeeAllLink';

type Props = {
    catalogStatus: string;
    categoryLinks: CategoryLink[];
    centerHeader: boolean;
    className: string;
    facets: ItemFacets;
    getMoreSimilarItems: () => void;
    isLoading: boolean;
    itemTitle: string;
    moreResultsAvailable: boolean;
    popularItemIds: number[];
    selectedItems: any;
    suggestions: Suggestions[];
    title?: ReactNode;
    width: number;
};

const SimilarItemsComponent = (props: Props) => {
    const {
        categoryLinks,
        centerHeader = false,
        className = '',
        facets,
        getMoreSimilarItems,
        isLoading,
        itemTitle,
        moreResultsAvailable,
        popularItemIds,
        selectedItems,
        suggestions,
        title,
        width,
    } = props;

    const formatMessage = useAppFormatMessage();
    const similarItemsUrl = getSimilarItemsUrl(itemTitle);
    const saveSearchParams = getBroadTitleSearchParams(itemTitle);

    const showPopular = selectedItems.length === 0;

    let linkMessage;
    let headerMessage;

    if (showPopular) {
        linkMessage = formatMessage(['see_all']);
        headerMessage = formatMessage(['bid_most_popular_items']);
    } else {
        headerMessage = title || formatMessage(['discover_similar_items']);
        linkMessage = formatMessage(['see_all']);
    }

    return (
        <div className={cn('block print:hidden', className)}>
            <div
                className={cn(
                    'flex flex-row items-center p-0',
                    '[&_h2]:mr-[15px] [&_h2]:mb-2xs [&_h2]:inline-block',
                    centerHeader && '[&_h2]:text-center',
                    'xsMax:[&_h2]:mr-0',
                    'xsMax:mb-xs xsMax:flex-col xsMax:items-start'
                )}
                key="header"
            >
                <H2>{headerMessage}</H2>
                <div className="min-[769px]:ml-auto flex flex-row items-center">
                    <SaveSearchContainer
                        allSearchParams={saveSearchParams}
                        className="mr-xs"
                        saveSearchParams={saveSearchParams}
                        small
                    />
                    <SeeAllLink
                        text={linkMessage}
                        url={similarItemsUrl}
                    />
                </div>
            </div>
            <DiscoveryTags
                categoryLinks={categoryLinks}
                facets={facets}
                key="discoveryTags"
                suggestions={suggestions}
                width={width}
            />
            {isLoading ? (
                <Loading />
            ) : (
                <FluidItemGrid
                    itemCardOption={upcomingItemPageItemCard}
                    itemIds={showPopular ? popularItemIds : selectedItems}
                    key="grid"
                />
            )}
            {moreResultsAvailable && (
                <Button
                    className="mx-auto mt-lg flex"
                    onClick={getMoreSimilarItems}
                >
                    {formatMessage(['loadMore'])}
                </Button>
            )}
        </div>
    );
};

export const SimilarItems = memo<Props>(SimilarItemsComponent, isEqual);
